import { useRouteShellContext } from "@components/routeShellContext"
import { calculateLanguagePercentage } from "@lib/helper"
import * as m from "@paraglide/messages"
import type { WorkDetailsProps } from "@site-types/components/workDetails"
import type {
  ContentImage,
  SupportedLocale as Lang,
  WorkPageSettings,
  WorkProductItem,
  WorkTagsItem,
} from "@site-types/content"
import type { GalleryImage } from "@site-types/workGallery"
import { imageAlt, imageDimensions, imageTitle, imageUrl, localized, tagsFor } from "../componentUtils"
import Tag from "../Tag"
import EsportsPlayer from "./EsportsPlayer"
import GridGallery from "./GridGallery"
import ScrollGallery from "./ScrollGallery"
import WorkSection from "./WorkSection"

const workPageVideoPoster = "/images/home/gamers.webp"

const localWorkSettings: Record<Lang, WorkPageSettings> = {
  en: {
    language: "en",
    workPageVideoPoster,
    workPageVideoBackupUrl: "/videos/work/esports.mp4",
    workPageVideoUrl: "https://www.youtube.com/embed/8ko7vCgKOcI",
  },
  ja: {
    language: "ja",
    workPageVideoPoster,
    workPageVideoBackupUrl: "/videos/work/esports.mp4",
    workPageVideoUrl: "https://www.youtube.com/embed/8ko7vCgKOcI",
  },
}

export function WorkDetails({ data }: WorkDetailsProps) {
  const { locale } = useRouteShellContext()
  const marketing = data.workMarketing
  const products = localized<WorkProductItem>(data.workProducts, locale)
  const [workTags] = localized<WorkTagsItem>(data.workTags ?? [], locale)
  const cmsSettings = data.workSettings?.[0] ?? localWorkSettings[locale]
  const settings = validateWorkSettings(
    locale,
    mergeWorkSettings(locale, cmsSettings, localWorkSettings[locale])
  )

  const workPageVideoUrl = settings.workPageVideoUrl as string
  const workPageVideoPoster =
    (settings.workPageVideoPoster as string | undefined) ?? localWorkSettings[locale].workPageVideoPoster
  const workPageVideoBackupUrl = settings.workPageVideoBackupUrl as string | undefined
  const workPageVideoTags = (workTags?.esportsBroadcastTags ?? []).filter((tag) => {
    const { englishPercentage, japanesePercentage } = calculateLanguagePercentage(tag)
    if (locale === "en" && japanesePercentage > 75) return false
    if (locale === "ja" && englishPercentage > 75) return false
    return true
  })
  const marketingTags = dashboardTagsOrFallback(
    workTags?.gameCultureMarketingTags,
    tagsFor(
      marketing.map((item) => item.coverImage),
      locale
    )
  )
  const productTags = dashboardTagsOrFallback(
    workTags?.productTags,
    tagsFor(
      products.map((item) => item.coverImage),
      locale,
      {
        filterByLanguage: false,
      }
    )
  )
  const marketingGalleryItems = marketing.map((value, key) => ({
    id: value.id ?? `gallery-item-${key}`,
    label: String(value.title ?? imageAlt(value.coverImage, locale) ?? `Marketing project ${key + 1}`),
    coverSrc: value.coverImage ? imageUrl(value.coverImage, "workCover") || "" : "",
    coverAlt: imageAlt(value.coverImage, locale),
    coverTitle: imageTitle(value.coverImage, locale),
    coverDimensions: imageDimensions(value.coverImage),
    images: ((value.gallery as ContentImage[] | undefined) ?? []).filter(Boolean).map((galleryImg) => ({
      src: imageUrl(galleryImg, "workModal") || "",
      alt: imageAlt(galleryImg, locale),
      title: imageTitle(galleryImg, locale),
      dimensions: imageDimensions(galleryImg),
    })),
  }))

  return (
    <>
      <WorkSection
        className="pr-[1em] max-sm:[&>div>ul:last-child]:pb-[5rem]"
        sectionId="esports"
        title={m.work_heading_esports({}, { locale: locale })}
      >
        <EsportsPlayer
          videoUrl={workPageVideoUrl}
          backupVideoUrl={workPageVideoBackupUrl}
          posterUrl={workPageVideoPoster}
        />
        <Tag data={workPageVideoTags} />
      </WorkSection>
      <WorkSection
        className="max-sm:[&>div>ul:last-child]:pb-[5rem]"
        sectionId="marketing"
        title={m.work_heading_marketing({}, { locale: locale })}
      >
        <ScrollGallery items={marketingGalleryItems} />
        <Tag data={marketingTags} />
      </WorkSection>
      <WorkSection
        sectionId="products"
        title={m.work_heading_products({}, { locale: locale })}
      >
        <GridGallery products={products} />
        <Tag data={productTags} />
      </WorkSection>
    </>
  )
}

function dashboardTagsOrFallback(tags: string[] | undefined, fallback: string[]) {
  return Array.isArray(tags) ? tags : fallback
}

function hasValue(value: string | null | undefined) {
  return typeof value === "string" && value.trim().length > 0
}

function mergeWorkSettings(
  locale: Lang,
  cmsSettings: WorkPageSettings,
  fallback: WorkPageSettings
): WorkPageSettings {
  return {
    language: cmsSettings.language ?? fallback.language ?? locale,
    workPageVideoPoster: hasValue(cmsSettings.workPageVideoPoster as string | undefined)
      ? cmsSettings.workPageVideoPoster
      : fallback.workPageVideoPoster,
    workPageVideoBackupUrl: hasValue(cmsSettings.workPageVideoBackupUrl as string | undefined)
      ? cmsSettings.workPageVideoBackupUrl
      : fallback.workPageVideoBackupUrl,
    workPageVideoUrl: hasValue(cmsSettings.workPageVideoUrl as string | undefined)
      ? cmsSettings.workPageVideoUrl
      : fallback.workPageVideoUrl,
  }
}

function validateWorkSettings(locale: Lang, settings?: WorkPageSettings): WorkPageSettings {
  if (!settings) {
    throw new Error(`Missing workSettings payload for locale "${locale}".`)
  }

  const missing: string[] = []
  if (!hasValue(settings.workPageVideoUrl as string | undefined)) missing.push("workPageVideoUrl")

  if (missing.length > 0) {
    throw new Error(`Missing required workSettings field "${missing[0]}" for locale "${locale}".`)
  }

  return settings
}

export default WorkDetails
